/**
 * What the browser knows about an error, kept so the assistant can be asked about it.
 *
 * The CRM shows an error as a toast — "Oops, something went wrong." — and the reason is somewhere
 * the user cannot see: in the server's record of the request (`request_error`, found by the id
 * every request carries), or, when the screen itself broke, in the console. This module keeps the
 * last few of each for a few minutes, decides which toast an "Ask the assistant" button belongs on,
 * and hands what it kept to the assistant's window when that button is pressed.
 *
 * Nothing here is sent anywhere until the user presses the button, and then only what
 * `errorContext` builds: the screen's path, the message shown, the failed request's function and
 * status, a crash's message and stacks, and the console lines just before — clipped, with anything
 * that looks like a credential masked. Request parameters are never taken: the server already has
 * the one value an error quotes back, and the rest is not needed to explain it.
 */

/* Sent on every PopLeads request; the backend writes it into request_error. */
export const REQUEST_ID_HEADER = "X-Request-Id";

/* The assistant's own requests. A failure there is the assistant's to report, in its own thread,
   and a button offering to ask the assistant about the assistant failing would only loop. */
const ASSISTANT_FNS = new Set([
    "askAssistant",
    "assistantUsage",
    "assistantRecentErrors",
    "kbSearch",
    "kbArticle",
    "listAssistantConversations",
    "saveAssistantConversation",
    "deleteAssistantConversation",
]);

/* A toast this soon after a failure is taken to be about it. Screens toast from the request's own
   catch block, so the gap is a few milliseconds; three seconds leaves room for a slow render. */
export const TOAST_WINDOW_MS = 3000;
/* How long anything is kept: long enough to press the button on a toast that lingered. */
const KEEP_MS = 5 * 60 * 1000;
const MAX_REQUESTS = 20;
const MAX_CONSOLE = 30;
const CONSOLE_SENT = 15;
const MAX_TEXT = 300;
const MAX_CONSOLE_TEXT = 500;
const MAX_STACK = 2000;

const requests = [];
const consoleLines = [];
const runtimeErrors = [];
const handlers = [];
let consoleInstalled = false;

const now = () => Date.now();

/* Where the user was when it failed. Read at the moment of failure, because by the time the
   button is pressed the screen may have moved on — util.handleErrorResponse sends a failed load
   straight to /error/404 or /error/500. */
const currentRoute = () => (typeof window !== "undefined" ? window.location.pathname : undefined);

const fresh = (list) => {
    const cutoff = now() - KEEP_MS;
    while (list.length > 0 && list[0].at < cutoff) list.shift();
    return list;
};

const keep = (list, entry, max) => {
    list.push(entry);
    while (list.length > max) list.shift();
};

/** A random id per request, readable in the server's table. */
export const newRequestId = () => {
    const c = typeof window !== "undefined" ? window.crypto : undefined;
    if (c?.randomUUID) return c.randomUUID();
    if (c?.getRandomValues) {
        const bytes = c.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
    return `${now().toString(16)}-${Math.random()
        .toString(16)
        .slice(2, 14)}`;
};

/* The same three shapes the backend masks: a bearer token, a long hex or base64 run, and a
   secret-looking name followed by its value. */
const BEARER = /bearer\s+[^\s"',]+/gi;
const LONG_RUN = /[A-Za-z0-9_\-+/=]{32,}/g;
const SECRET_KV = /(\w*(?:token|sid|password|parola|secret|authorization|apikey|api_key|cookie)\w*)(["']?\s*[=:]\s*["']?)[^\s,&"'}]+/gi;

export const mask = (text, max = MAX_TEXT) => {
    if (text === undefined || text === null) return undefined;
    const masked = String(text)
        .replace(BEARER, "Bearer ***")
        .replace(SECRET_KV, "$1$2***")
        .replace(LONG_RUN, "***");
    return masked.length > max ? `${masked.slice(0, max)}…` : masked;
};

const fnOf = (url) => {
    const path = String(url || "").split("?")[0];
    const at = path.indexOf("/api/ws/");
    return at < 0 ? null : path.slice(at + "/api/ws/".length).split("/")[0] || null;
};

/* Same-origin only: a custom header makes a cross-origin request preflight, and a server that does
   not list it in its CORS headers would then refuse a call that works today. Every PopLeads call
   goes through RequestService's relative `/api/ws`, so nothing real is left out. */
export const isPopleadsRequest = (config) => {
    const url = String(config?.url || "");
    if (fnOf(url) === null) return false;
    if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) return true;
    return typeof window !== "undefined" && url.startsWith(`${window.location.origin}/`);
};

/**
 * One request that did not succeed: an HTTP error, or an HTTP 200 whose body said nok. The 401
 * that ends a session, and the assistant's own calls, are not kept.
 */
export const recordFailedRequest = ({ config, status, message }) => {
    const fn = fnOf(config?.url);
    if (!fn || ASSISTANT_FNS.has(fn) || status === 401) return;
    keep(
        requests,
        {
            at: now(),
            route: currentRoute(),
            requestId: config?.headers?.[REQUEST_ID_HEADER] || null,
            fn,
            method: String(config?.method || "post").toUpperCase(),
            status: status ?? null,
            /* No status means the request never got an answer — offline, a timeout — so the server
               has no row for it and the id is not worth sending. */
            reachedServer: status !== null && status !== undefined,
            message: mask(message),
            claimed: false,
        },
        MAX_REQUESTS
    );
};

/** A JS value as one line of console text, without ever serialising a request's config. */
export const describe = (value) => {
    if (value === undefined) return "undefined";
    if (value === null) return "null";
    if (value?.isAxiosError || (value?.config && value?.request)) {
        return `Request failed: ${fnOf(value.config?.url) || "request"} ${value.response?.status ?? "(no response)"}`;
    }
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    if (typeof value === "string") return value;
    if (typeof value !== "object") return String(value);
    try {
        const seen = new WeakSet();
        return JSON.stringify(value, (key, v) => {
            if (/token|sid|password|secret|authorization|cookie|headers/i.test(key)) return "***";
            if (v && typeof v === "object") {
                if (seen.has(v)) return "[circular]";
                seen.add(v);
            }
            return v;
        });
    } catch (e) {
        return Object.prototype.toString.call(value);
    }
};

/* A thrown Error that is not a request's: the screen's own code broke. */
const isRuntimeError = (value) => value instanceof Error && !value.isAxiosError && !value.config;

export const recordConsole = (level, args) => {
    const parts = Array.from(args || []);
    keep(consoleLines, { at: now(), level, text: mask(parts.map(describe).join(" "), MAX_CONSOLE_TEXT) }, MAX_CONSOLE);
    const thrown = parts.find(isRuntimeError);
    if (level === "error" && thrown) recordRuntimeError(thrown);
};

/** An error as it may leave the browser: its name, and its message and stacks masked and clipped. */
export const crashOf = (error, componentStack) => ({
    name: error?.name || "Error",
    message: mask(error?.message ?? describe(error)),
    stack: mask(error?.stack, MAX_STACK),
    componentStack: mask(componentStack, MAX_STACK),
});

/** A crash, or an uncaught error: something in the page itself rather than on the server. */
export const recordRuntimeError = (error, componentStack) => {
    keep(
        runtimeErrors,
        { at: now(), route: currentRoute(), ...crashOf(error, componentStack), claimed: false },
        MAX_REQUESTS
    );
};

/**
 * console.error, uncaught errors and unhandled rejections, kept as they happen. Once per page —
 * the console is the page's, not a component's.
 */
export const installConsoleCapture = () => {
    if (consoleInstalled || typeof window === "undefined") return;
    consoleInstalled = true;
    /* Wrapping console.error is the point here. */
    /* eslint-disable no-console */
    const original = console.error;
    console.error = (...args) => {
        try {
            recordConsole("error", args);
        } catch (e) {
            /* Never let keeping a line cost the line itself. */
        }
        return original.apply(console, args);
    };
    /* eslint-enable no-console */
    window.addEventListener("error", (event) => {
        if (event?.error) recordRuntimeError(event.error);
        keep(consoleLines, { at: now(), level: "error", text: mask(event?.message, MAX_CONSOLE_TEXT) }, MAX_CONSOLE);
    });
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event?.reason;
        if (isRuntimeError(reason)) recordRuntimeError(reason);
        keep(
            consoleLines,
            { at: now(), level: "error", text: mask(`Unhandled rejection: ${describe(reason)}`, MAX_CONSOLE_TEXT) },
            MAX_CONSOLE
        );
    });
};

const latestUnclaimed = (list, withinMs) => {
    const cutoff = now() - withinMs;
    const items = fresh(list);
    for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].at < cutoff) return null;
        if (!items[i].claimed) return items[i];
    }
    return null;
};

/**
 * What an error the user is about to be shown is about, if anything: the newest failure of the
 * last few seconds that nothing else has taken — a toast, or the 404 / 500 page a failed load is
 * sent to. A request failure wins over a runtime error, since a screen that reports a failed
 * request usually logs it too. Claimed here, so two toasts for one failure put the button on the
 * first only. Null when there is nothing behind it — a form saying "Please select marketing
 * channel" is its own explanation.
 */
export const claimRecentFailure = (withinMs = TOAST_WINDOW_MS, { orLatestOnScreen = false } = {}) => {
    const request = latestUnclaimed(requests, withinMs);
    if (request) {
        request.claimed = true;
        return { request };
    }
    const crash = latestUnclaimed(runtimeErrors, withinMs);
    if (crash) {
        crash.claimed = true;
        return { crash };
    }
    return orLatestOnScreen ? latestOnScreen(withinMs) : null;
};

/* Several cards can show an error for one failed request — an account page whose account did not
   load. The first card takes the request; the rest share it, as long as it failed on this screen. */
const latestOnScreen = (withinMs) => {
    const cutoff = now() - withinMs;
    const route = currentRoute();
    const items = fresh(requests);
    for (let i = items.length - 1; i >= 0 && items[i].at >= cutoff; i--) {
        if (items[i].route === route) return { request: items[i] };
    }
    return null;
};

/**
 * What goes to the assistant with the question: `requestId` for the server's row, and the
 * browser's record as `context`. The backend keeps only these fields of it, and clips them again.
 */
export const errorContext = ({ request, crash, shown } = {}) => {
    const context = {
        route: request?.route || crash?.route || currentRoute(),
        at: new Date().toISOString(),
    };
    if (typeof shown === "string" && shown.trim()) context.shown = mask(shown.trim());
    if (request) {
        context.request = {
            fn: request.fn,
            method: request.method,
            status: request.status === null ? "no response" : String(request.status),
            message: request.message,
        };
    }
    if (crash) {
        context.crash = {
            name: crash.name,
            message: crash.message,
            stack: crash.stack,
            componentStack: crash.componentStack,
        };
    }
    const lines = fresh(consoleLines).slice(-CONSOLE_SENT);
    if (lines.length > 0) {
        context.console = lines.map((line) => ({
            level: line.level,
            at: new Date(line.at).toISOString(),
            text: line.text,
        }));
    }
    return {
        requestId: request?.reachedServer ? request.requestId : null,
        context,
    };
};

/**
 * The assistant's window registers here while it is mounted, and the button hands the error to
 * whichever registered window takes it. Newest first, and a handler returns false to pass — the
 * subheader and a page's action bar can each mount one, and only one may open.
 */
export const onAskAboutError = (handler) => {
    handlers.push(handler);
    return () => {
        const i = handlers.indexOf(handler);
        if (i >= 0) handlers.splice(i, 1);
    };
};

export const canAskAboutError = () => handlers.length > 0;

/** `options.reopen`: open the conversation already started about the same request, if there is one. */
export const askAboutError = (error, options) => {
    for (let i = handlers.length - 1; i >= 0; i--) {
        if (handlers[i](error, options) !== false) return true;
    }
    return false;
};

/** For tests: forget everything kept. */
export const resetErrorCapture = () => {
    requests.length = 0;
    consoleLines.length = 0;
    runtimeErrors.length = 0;
};
